import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { RV_CAMPSITE_BUNDLES_LANDING_PAGE } from '@/constants/urls';
import { useRouter } from '@/hooks/useRouter';
import { TRootState } from '@/redux/rootReducer';
import { getUser } from '@/redux/selectors/auth/user';
import { getBookingData } from '@/redux/selectors/checkout/booking';
import { getCheckoutSteps } from '@/redux/selectors/checkout/checkoutSteps';
import { getCheckoutError } from '@/redux/selectors/checkout/error';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getQuoteId } from '@/redux/selectors/quote';
import { ECheckoutSteps } from '@/services/types/core/checkoutSteps';
import { getReferrer } from '@/utility/getReferrer';
import { getParamAsString } from '@/utility/queryParams';

import { useModifiedCheckoutSteps } from '../BookingSteps/useCheckoutSteps';
import Header from './Header';

type THeaderContainerProps = {
  hasError?: boolean;
  isAuto?: boolean;
};

const HeaderContainer = ({ hasError, isAuto }: THeaderContainerProps) => {
  const router = useRouter();
  const intl = useIntl();
  const listingSlug = useSelector<TRootState, string | undefined>(
    state => state.listing.data?.slug,
  );
  const queryParams = useSelector(getQueryParams);
  const booking = useSelector(getBookingData);
  const user = useSelector(getUser);
  const quoteId = useSelector(getQuoteId);
  const quote = quoteId || queryParams.quote;
  const checkoutSteps = useSelector(getCheckoutSteps);
  const bookingError = useSelector(getCheckoutError);
  const step = getParamAsString(router.query.step);

  // Calculate modified checkout steps with injected Trip Insurance step for backwards compatibility
  const modifiedCheckoutSteps = useModifiedCheckoutSteps({
    checkoutSteps,
    // We are not going to use the completeness of virtual steps
    completedVirtualSteps: new Set<ECheckoutSteps>(),
    booking,
    userId: user?.id,
  });

  const previousPageUrl =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('previousPageUrl') || getReferrer() || null
      : null;

  const pathToCheck = RV_CAMPSITE_BUNDLES_LANDING_PAGE;

  const bundledListingPageUrl = booking?.deliverable_campground?.vacation_package_slug
    ? `${RV_CAMPSITE_BUNDLES_LANDING_PAGE}/${booking.deliverable_campground.vacation_package_slug?.split('/').pop()}`
    : null;

  let isVacationPackagePage = false;

  if (typeof previousPageUrl === 'string' && previousPageUrl.trim()) {
    try {
      const parsedUrl = new URL(previousPageUrl);
      isVacationPackagePage = parsedUrl.pathname.startsWith(pathToCheck);
    } catch {
      // do nothing
    }
  }

  if (
    booking?.id &&
    'sibling_booking_group_id' in booking &&
    Boolean(booking.sibling_booking_group_id)
  ) {
    isVacationPackagePage = true;
  }

  let backLinkUrl = quote
    ? `${isAuto ? '/auto' : ''}${listingSlug}?quote=${quote}`
    : `${isAuto ? '/auto' : ''}${listingSlug}`;

  const previousStep = useMemo(() => {
    const currentStepIndex = modifiedCheckoutSteps?.steps?.findIndex(s => s.name === step);
    return modifiedCheckoutSteps?.steps?.[(currentStepIndex || 0) - 1]?.name || null;
  }, [modifiedCheckoutSteps, step]);

  if (previousStep) {
    backLinkUrl = `${isAuto ? '/auto' : ''}/checkout/${booking?.id}/${previousStep}`;
  }

  if (isVacationPackagePage) {
    backLinkUrl = bundledListingPageUrl || previousPageUrl || backLinkUrl;
  }

  // Get dynamic back link label based on whether we're going to previous step or listing
  const backLinkTitle = useMemo(() => {
    const backLinksTitleList = [
      intl.formatMessage({
        defaultMessage: 'Back to listing page',
        id: '//Gljv',
        description: 'Checkout > Header > back link title',
      }),
      intl.formatMessage({
        defaultMessage: 'Back to Booking Request',
        id: 'E6Y3WF',
        description: 'Checkout > Header > back link title',
      }),
      ...(modifiedCheckoutSteps?.steps?.some(s => s.name === ECheckoutSteps.QUESTIONS)
        ? [
            intl.formatMessage({
              defaultMessage: 'Back to Questions',
              id: 'vYL95Z',
              description: 'Checkout > Header > back link title',
            }),
          ]
        : []),
      ...(modifiedCheckoutSteps?.steps?.some(s => s.name === ECheckoutSteps.TRIP_INSURANCE)
        ? [
            intl.formatMessage({
              defaultMessage: 'Back to Extra Protection',
              id: 'EWn7Jg',
              description: 'Checkout > Header > back link title',
            }),
          ]
        : []),
      intl.formatMessage({
        defaultMessage: 'Back to Payment',
        id: 'N5r+x1',
        description: 'Checkout > Header > back link title',
      }),
    ];

    const currentStepIndex = modifiedCheckoutSteps?.steps?.findIndex(s => s.name === step) || -1;
    return backLinksTitleList[currentStepIndex] || backLinksTitleList[0];
  }, [intl, modifiedCheckoutSteps, step]);

  return (
    <Header
      key={String(bookingError)}
      hasError={hasError}
      backLinkUrl={backLinkUrl}
      backLinkTitle={backLinkTitle}
      isAuto={isAuto}
    />
  );
};

export default HeaderContainer;
